import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import MainLayout from './components/Layout/MainLayout';

// Static imports (common entry points)
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import NotFound from './pages/NotFound/NotFound';

// Lazy-loaded pages
const SignUp = React.lazy(() => import('./pages/SignUp/SignUp'));
const FAQ = React.lazy(() => import('./pages/FAQ/FAQ'));
const Contact = React.lazy(() => import('./pages/Contact/Contact'));
const Scrumboard = React.lazy(() => import('./pages/Scrumboard/Scrumboard'));
const Sprints = React.lazy(() => import('./pages/Sprints/Sprints'));
const Planning = React.lazy(() => import('./pages/Planning/Planning'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const UserProfile = React.lazy(() => import('./pages/UserProfile/UserProfile'));
const UserManagement = React.lazy(() => import('./pages/UserManagement/UserManagement'));
const ProjectManagement = React.lazy(() => import('./pages/ProjectManagement/ProjectManagement'));
const QueryManagement = React.lazy(() => import('./pages/QueryManagement/QueryManagement'));
const McpHelp = React.lazy(() => import('./pages/McpHelp/McpHelp'));
const ApiTest = React.lazy(() => import('./pages/ApiTest'));

function App() {
  console.log('[App] Initializing application');
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />

            {/* Logout route - shows login page after session destroyed by backend */}
            <Route path="/logout" element={<Login />} />

            {/* API Test Page - Non-protected for debugging */}
            <Route path="/api-test" element={<ApiTest />} />

            {/* Protected routes */}
            <Route
              path="/scrumboard/:cardId?"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Scrumboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sprints"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Sprints />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sprints/:sprintId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Sprints />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sprints/:sprintId/:cardId"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Sprints />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/planning/:cardId?"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Planning />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <Dashboard />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/:userid/:cardId?"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <UserProfile />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/usersmanagement"
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <UserManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projectmanagement"
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <ProjectManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/querymanagement"
              element={
                <ProtectedRoute requireAdmin>
                  <MainLayout>
                    <QueryManagement />
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/mcp-help"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <McpHelp />
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
